/**
 * ContextIndicator Component
 * Displays detected conversation context (project type, materials, issues)
 */

import React from 'react';

/**
 * Extract context from messages
 * Mirrors the backend conversation-service.js pattern detection
 */
function extractContext(messages) {
  const context = {
    projectType: null,
    materials: new Set(),
    issues: new Set(),
    specs: []
  };

  if (!messages || messages.length === 0) {
    return context;
  }

  // Material patterns
  const materialPatterns = /\b(PLA|PETG|ABS|PC|PEEK|PPS|Nylon|PA6|PA12|TPU|TPE|Dahltram|I-?350CF?|PC-?CF|carbon\s*fiber|CF|glass\s*fiber|GF)\b/gi;
  
  // Issue patterns
  const issuePatterns = /\b(warp|clog|under.?extrusion|over.?extrusion|layer\s*separation|delamination|stringing|adhesion|accuracy|temperature|heating|cooling)\b/gi;
  
  // Application patterns for project type
  const applicationPatterns = /\b(mold|tool|tooling|prototype|production|casting|injection|thermoform|calibrat)\b/gi;

  // Temperature patterns
  const tempPatterns = /\b(\d{2,3})\s*°?C\b/gi;

  // Nozzle size patterns
  const nozzlePatterns = /\b(\d{1,2})\s*mm\s*nozzle|\bnozzle\s*(\d{1,2})\s*mm/gi;

  // Process each message
  for (const msg of messages) {
    const content = msg.content || '';
    
    // Extract materials
    const materials = content.match(materialPatterns) || [];
    materials.forEach(m => context.materials.add(m.toUpperCase().replace(/\s+/g, '-')));

    // Extract issues
    const issues = content.match(issuePatterns) || [];
    issues.forEach(i => context.issues.add(i.toLowerCase().replace(/\s+/g, ' ')));

    // Detect project type from applications
    const apps = content.match(applicationPatterns) || [];
    for (const app of apps) {
      const appLower = app.toLowerCase();
      if (appLower.includes('mold') || appLower.includes('tool')) {
        context.projectType = 'Mold Making';
      } else if (appLower.includes('prototype')) {
        context.projectType = 'Prototyping';
      } else if (appLower.includes('production')) {
        context.projectType = 'Production';
      } else if (appLower.includes('calibrat')) {
        context.projectType = 'Calibration';
      }
    }

    // Extract specs (temps, nozzle sizes)
    const temps = content.match(tempPatterns) || [];
    temps.forEach(t => {
      if (!context.specs.includes(t)) {
        context.specs.push(t);
      }
    });

    const nozzles = content.match(nozzlePatterns) || [];
    nozzles.forEach(n => {
      const cleaned = n.replace(/nozzle/i, '').trim();
      if (!context.specs.includes(cleaned)) {
        context.specs.push(cleaned);
      }
    });
  }

  // If issues detected but no project type, set to troubleshooting
  if (!context.projectType && context.issues.size > 0) {
    context.projectType = 'Troubleshooting';
  }

  // Limit specs shown
  context.specs = context.specs.slice(0, 3);

  return {
    projectType: context.projectType,
    materials: Array.from(context.materials).slice(0, 3),
    issues: Array.from(context.issues).slice(0, 2),
    specs: context.specs
  };
}

function ContextIndicator({ messages = [] }) {
  const context = extractContext(messages);
  
  // Don't render if no context detected
  const hasContext = context.projectType || 
    context.materials.length > 0 || 
    context.issues.length > 0;
  
  if (!hasContext) {
    return null;
  }

  return (
    <div className="chatbot-context">
      {/* Project type */}
      {context.projectType && (
        <span className="chatbot-context-tag project">
          {context.projectType}
        </span>
      )}
      
      {/* Materials */}
      {context.materials.map((material, index) => (
        <span key={`mat-${index}`} className="chatbot-context-tag material">
          {material}
        </span>
      ))}
      
      {/* Issues */}
      {context.issues.map((issue, index) => (
        <span key={`issue-${index}`} className="chatbot-context-tag issue">
          {issue}
        </span>
      ))}
    </div>
  );
}

export default ContextIndicator;
export { extractContext };
